import type { ReleaseDownloadCatalogItemRecord } from '@/api/licensing';

export interface InstallerPlatformGroup {
  key: string;
  os: string;
  arch: string;
  label: string;
  items: ReleaseDownloadCatalogItemRecord[];
  latest: ReleaseDownloadCatalogItemRecord;
}

export interface InstallerAppGroup {
  appId: string;
  label: string;
  platforms: InstallerPlatformGroup[];
}

const FRIENDLY_APP_NAMES: Record<string, string> = {
  mailarchiver: 'MailArchiver',
};

const PLATFORM_ORDER: Record<string, number> = {
  'windows/amd64': 10,
  'windows/x64': 10,
  'win32/amd64': 10,
  'linux/amd64': 20,
  'linux/x64': 20,
  'darwin/arm64': 30,
  'macos/arm64': 30,
  'darwin/amd64': 40,
  'macos/amd64': 40,
};

export function buildInstallerCatalog(items: ReleaseDownloadCatalogItemRecord[]): InstallerAppGroup[] {
  const byApp = new Map<string, ReleaseDownloadCatalogItemRecord[]>();

  for (const item of items) {
    const existing = byApp.get(item.appId) ?? [];
    existing.push(item);
    byApp.set(item.appId, existing);
  }

  return [...byApp.entries()]
    .map(([appId, appItems]) => ({
      appId,
      label: formatAppLabel(appId),
      platforms: buildPlatformGroups(appItems),
    }))
    .filter((app) => app.platforms.length > 0)
    .sort((left, right) => left.label.localeCompare(right.label, 'ru', { numeric: true }));
}

export function filterInstallerApps(apps: InstallerAppGroup[], search: string): InstallerAppGroup[] {
  const query = search.trim().toLowerCase();
  if (!query) return apps;

  return apps.filter((app) => {
    const haystack = `${app.appId} ${app.label}`.toLowerCase();
    return haystack.includes(query);
  });
}

export function formatAppLabel(appId: string): string {
  const normalized = appId.trim();
  if (!normalized) return 'Программа';
  const friendly = FRIENDLY_APP_NAMES[normalized.toLowerCase()];
  if (friendly) return friendly;

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function platformKey(os: string, arch: string): string {
  return `${os.trim().toLowerCase()}/${arch.trim().toLowerCase()}`;
}

export function formatPlatformLabel(os: string, arch: string): string {
  const key = platformKey(os, arch);
  if (key === 'windows/amd64' || key === 'windows/x64' || key === 'win32/amd64') return 'Windows x64';
  if (key === 'linux/amd64' || key === 'linux/x64') return 'Linux x64';
  if (key === 'darwin/arm64' || key === 'macos/arm64') return 'macOS Apple';
  if (key === 'darwin/amd64' || key === 'macos/amd64') return 'macOS Intel';

  return `${os}/${arch}`;
}

export function releaseCatalogItemLabel(item: ReleaseDownloadCatalogItemRecord): string {
  return `${formatAppLabel(item.appId)} ${item.version} ${item.channel} · ${formatPlatformLabel(
    item.artifact.os,
    item.artifact.arch,
  )}`;
}

function buildPlatformGroups(items: ReleaseDownloadCatalogItemRecord[]): InstallerPlatformGroup[] {
  const byPlatform = new Map<string, ReleaseDownloadCatalogItemRecord[]>();

  for (const item of items) {
    const key = platformKey(item.artifact.os, item.artifact.arch);
    const existing = byPlatform.get(key) ?? [];
    existing.push(item);
    byPlatform.set(key, existing);
  }

  return [...byPlatform.entries()]
    .map(([key, platformItems]) => {
      const sorted = [...platformItems].sort(compareReleaseItemsByRecency);
      const latest = pickLatestStable(sorted);
      return {
        key,
        os: latest.artifact.os,
        arch: latest.artifact.arch,
        label: formatPlatformLabel(latest.artifact.os, latest.artifact.arch),
        items: sorted,
        latest,
      };
    })
    .sort(comparePlatformGroups);
}

function pickLatestStable(sortedItems: ReleaseDownloadCatalogItemRecord[]): ReleaseDownloadCatalogItemRecord {
  return sortedItems.find((item) => item.channel.toLowerCase() === 'stable') ?? sortedItems[0];
}

function comparePlatformGroups(left: InstallerPlatformGroup, right: InstallerPlatformGroup): number {
  const leftOrder = PLATFORM_ORDER[left.key] ?? 1000;
  const rightOrder = PLATFORM_ORDER[right.key] ?? 1000;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;
  return left.label.localeCompare(right.label, 'ru', { numeric: true });
}

function compareReleaseItemsByRecency(
  left: ReleaseDownloadCatalogItemRecord,
  right: ReleaseDownloadCatalogItemRecord,
): number {
  const rightTime = releaseTime(right);
  const leftTime = releaseTime(left);
  if (rightTime !== leftTime) return rightTime - leftTime;
  return right.version.localeCompare(left.version, undefined, { numeric: true, sensitivity: 'base' });
}

function releaseTime(item: ReleaseDownloadCatalogItemRecord): number {
  const raw = item.publishedAt || item.artifact.updatedAt || item.artifact.createdAt;
  const time = Date.parse(raw);
  return Number.isFinite(time) ? time : 0;
}
